import { useState, useMemo, useEffect } from "react";
import { selectedOptic, userData, focusElement } from "../js/utils";
import { useOptics } from "../hooks/useOptics";
import ButtonIconH from "./ButtonIconH";
import { CodeScanning } from "./CodeScanning";
import DataTable from "react-data-table-component";
import InputData from "./InputData";
import TooltipBox from "./Tooltip";
import Dropdown from "./Dropdown";
import {
  hearingAidsColumnsTransfer,
  lensesColumnsTransfer,
  contactLensesColumnsTransfer,
  liquidColumnsTransfer,
  frameColumnsTransfer,
  variousColumnsTransfer,
  sunglassesColumnsTransfer,
  unavailableProductColumnsTransfer,
} from "../js/tableColumns";
import { post } from "../js/post";

const familyConfig = [
  { title: "Audífonos", columns: hearingAidsColumnsTransfer },
  { title: "Lentes oftálmicas", columns: lensesColumnsTransfer },
  { title: "Lentes de contacto", columns: contactLensesColumnsTransfer },
  { title: "Líquidos", columns: liquidColumnsTransfer },
  { title: "Monturas", columns: frameColumnsTransfer },
  { title: "Varios", columns: variousColumnsTransfer },
  { title: "Gafas de sol", columns: sunglassesColumnsTransfer },
];

function injectStockInput(
  columns,
  setProductList,
  createPopupQuestion,
  removePopupQuestion,
) {
  return columns.map((col) => {
    if (col.id === "stock-transfer") {
      return {
        ...col,
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <button
              className="incdec-btn"
              onClick={() =>
                setProductList((prev) =>
                  prev.map((p) => {
                    const isTarget =
                      row.id === -1 ? p.codigo === row.codigo : p.id === row.id;
                    return isTarget
                      ? {
                          ...p,
                          stockTraspaso: Math.max(1, p.stockTraspaso - 1),
                        }
                      : p;
                  }),
                )
              }
            >
              -
            </button>
            <div style={{ flex: 1 }}>
              <InputData
                type="type-integer"
                size={0}
                input={row.stockTraspaso}
                onChange={(e) =>
                  setProductList((prev) =>
                    prev.map((p) => {
                      const isTarget =
                        row.id === -1
                          ? p.codigo === row.codigo
                          : p.id === row.id;
                      return isTarget
                        ? { ...p, stockTraspaso: parseInt(e) || 1 }
                        : p;
                    }),
                  )
                }
              ></InputData>
            </div>
            <button
              className="incdec-btn"
              onClick={() =>
                setProductList((prev) =>
                  prev.map((p) => {
                    const isTarget =
                      row.id === -1 ? p.codigo === row.codigo : p.id === row.id;
                    return isTarget
                      ? { ...p, stockTraspaso: p.stockTraspaso + 1 }
                      : p;
                  }),
                )
              }
            >
              +
            </button>
          </div>
        ),
      };
    }
    if (col.name === "") {
      return {
        ...col,
        cell: (row) => (
          <>
            <button
              onClick={() => {
                createPopupQuestion(
                  "¿Desea eliminar el producto?",
                  () => {
                    setProductList((prev) =>
                      prev.filter((p) =>
                        row.id === -1
                          ? p.codigo !== row.codigo
                          : p.id !== row.id,
                      ),
                    );
                    removePopupQuestion();
                  },
                  removePopupQuestion,
                  "Sí",
                  "No",
                );
              }}
            >
              <img
                src={`/images/remove-blue.svg`}
                data-tooltip-id={row.id}
                className="icon-pref"
                alt="Eliminar"
              ></img>
            </button>
            <TooltipBox
              id={String(row.id)}
              content={
                <div className="opt-table-tooltip grid-5">
                  <div className="cell combined-cells-5 cell-start-content">
                    Borrar
                  </div>
                </div>
              }
            ></TooltipBox>
          </>
        ),
      };
    }
    return col;
  });
}

const conditionalRowStyles = [
  {
    when: (row) => row.id === -1,
    style: { backgroundColor: "#ffdfbb" },
  },
];

export function StockTransfer({
  createPopupQuestion,
  removePopupQuestion,
  onClose,
}) {
  const opticList = useOptics();
  const opticId = selectedOptic?.id ?? "";
  const LS_KEY = `stockTransfer_${opticId}_productList`;
  const LS_ORIGIN = `stockTransfer_${opticId}_originOptic`;
  const LS_DESTINATION = `stockTransfer_${opticId}_destinationOptic`;

  const [productList, setProductList] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [originOptic, setOriginOptic] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_ORIGIN);
      return saved
        ? JSON.parse(saved)
        : (selectedOptic ?? { id: "", text: "" });
    } catch {
      return selectedOptic ?? { id: "", text: "" };
    }
  });
  const [destinationOptic, setDestinationOptic] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_DESTINATION);
      return saved ? JSON.parse(saved) : { id: "", text: "" };
    } catch {
      return { id: "", text: "" };
    }
  });

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(productList));
  }, [productList]);

  useEffect(() => {
    localStorage.setItem(LS_ORIGIN, JSON.stringify(originOptic));
  }, [originOptic]);

  useEffect(() => {
    localStorage.setItem(LS_DESTINATION, JSON.stringify(destinationOptic));
  }, [destinationOptic]);

  const validProducts = useMemo(
    () => productList.filter((e) => e.id !== -1),
    [productList],
  );

  async function transferProductStock() {
    if (!destinationOptic.id) {
      createPopupQuestion(
        "Debe seleccionar una óptica destino antes de realizar el traspaso.",
        () => {
          removePopupQuestion();
          setTimeout(() => focusElement(41), 0);
        },
        null,
        "Entendido",
      );
      return;
    }
    const count = validProducts.length;
    const plural = count !== 1 ? "s" : "";
    createPopupQuestion(
      `Se va a realizar un traspaso de ${count} producto${plural}. ¿Desea continuar?`,
      async () => {
        await post(
          "support-extra/traspasosMultiples/TraspasarStockProductos.php",
          {
            token: userData.token,
            opticaOrigen: originOptic.id,
            opticaDestino: destinationOptic.id,
            productos: validProducts.map((e) => ({
              codigo: e.codigo,
              stock: e.stockTraspaso,
            })),
          },
        );
        removePopupQuestion();
        createPopupQuestion(
          `El traspaso de stock se ha realizado de forma correcta.`,
          () => {
            removePopupQuestion();
            clearProductList();
          },
          null,
          "Entendido",
        );
      },
      removePopupQuestion,
      "Sí",
      "No",
    );
  }

  function clearProductList() {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_DESTINATION);
    setProductList([]);
    setDestinationOptic({ id: "", text: "" });
  }

  const productsByFamily = useMemo(() => {
    const available = familyConfig
      .map((config, familia) => ({
        ...config,
        items: productList.filter((p) => p.familia === familia),
      }))
      .filter((group) => group.items.length > 0);
    const unavailable = productList.filter((p) => p.familia === "");
    if (unavailable.length > 0) {
      available.push({
        title: "Productos no disponibles",
        columns: unavailableProductColumnsTransfer,
        items: unavailable,
      });
    }
    return available;
  }, [productList]);

  return (
    <div className="popup-container">
      <div className="ap-container popup-screen" style={{ width: "90%" }}>
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">Traspasos</span>
          <button className="popup-action-icon close-icon" onClick={onClose}>
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className="tab-content ap-content"
          style={{ overflow: "unset", backgroundColor: "#f9f9f9" }}
        >
          <div
            className="container-panel"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="opt-table-content grid-5" id="transfer-grid">
              <div className="cell cell-label cell-start-content">
                Óptica origen
              </div>
              <div className="cell">
                <Dropdown
                  disabled={true}
                  tabIndex={40}
                  optionList={opticList.filter(
                    (o) => o.id !== destinationOptic.id,
                  )}
                  size={0}
                  op={originOptic.id}
                  onChange={(e) => setOriginOptic(e)}
                />
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Óptica destino
              </div>
              <div className="cell">
                <Dropdown
                  tabIndex={41}
                  optionList={[
                    { id: "", text: "" },
                    ...opticList.filter((o) => o.id !== originOptic.id),
                  ]}
                  size={0}
                  op={destinationOptic.id}
                  onChange={(e) => setDestinationOptic(e)}
                />
              </div>
            </div>
            <CodeScanning
              opticalId={originOptic.id}
              onRegister={(product) => {
                setProductList((prev) => {
                  const isSame = (p) =>
                    product.id === -1
                      ? p.codigo === product.codigo
                      : p.id === product.id;
                  if (prev.some(isSame)) {
                    return prev.map((p) =>
                      isSame(p)
                        ? {
                            ...p,
                            stockTraspaso:
                              p.stockTraspaso + product.stockTraspaso,
                          }
                        : p,
                    );
                  }
                  return [...prev, product];
                });
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                maxHeight: 750,
                paddingRight: 5,
                marginBottom: 10,
              }}
            >
              {productsByFamily.map((group) => (
                <div
                  key={group.title}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginTop: 20,
                  }}
                >
                  <div style={{ minWidth: "1350px" }}>
                    <div className="opt-table-title">
                      {group.title} ({group.items.length})
                    </div>
                    <DataTable
                      columns={injectStockInput(
                        group.columns,
                        setProductList,
                        createPopupQuestion,
                        removePopupQuestion,
                      )}
                      data={group.items}
                      className="custom-datatable"
                      noDataComponent="No hay productos disponibles."
                      conditionalRowStyles={conditionalRowStyles}
                    />
                  </div>
                </div>
              ))}
            </div>
            {productList.length > 0 && (
              <div
                className="popup-buttons"
                style={{ justifyContent: "center" }}
              >
                <ButtonIconH
                  text="Limpiar"
                  onClick={() => {
                    createPopupQuestion(
                      "Se eliminarán todos los productos leídos. ¿Desea continuar?",
                      () => {
                        clearProductList();
                        removePopupQuestion();
                      },
                      removePopupQuestion,
                      "Sí",
                      "No",
                    );
                  }}
                ></ButtonIconH>
                <ButtonIconH
                  text="Realizar traspaso"
                  onClick={transferProductStock}
                ></ButtonIconH>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
